import { supabase } from './supabase'

// ─── ACTIVITY LOG TYPES ───────────────────────────────────────────────────────

export type ActivityType =
  | 'register'
  | 'login'
  | 'login_failed'
  | 'logout'
  | 'payment_attempt'
  | 'payment_success'
  | 'payment_failed'
  | 'plan_change'
  | 'account_suspended'
  | 'account_activated'
  | 'bot_enabled'
  | 'bot_disabled'
  | 'profile_update'
  | 'password_reset'
  | 'ticket_created'
  | 'admin_action'

export interface ActivityLogEntry {
  id?: string
  user_email: string
  activity_type: ActivityType
  description: string
  description_ar: string
  metadata?: Record<string, any>
  ip_address?: string
  user_agent?: string
  performed_by?: string // 'user' | admin email
  created_at?: string
}

// ─── ACTIVITY LOG OPERATIONS ──────────────────────────────────────────────────

export const dbActivityLog = {
  /**
   * Log a new activity
   */
  log: async (
    entry: Omit<ActivityLogEntry, 'id' | 'created_at'>,
  ): Promise<boolean> => {
    try {
      // Add browser info
      const enrichedEntry = {
        ...entry,
        user_email: entry.user_email.toLowerCase(),
        user_agent:
          typeof navigator !== 'undefined'
            ? navigator.userAgent.substring(0, 200)
            : '',
        performed_by: entry.performed_by || 'user',
      }

      const { error } = await supabase
        .from('activity_logs')
        .insert([enrichedEntry])

      if (error) {
        console.error('Activity log error:', error)
        // Don't throw - logging should never break the app
        return false
      }
      return true
    } catch (e) {
      console.error('Activity log failed:', e)
      return false
    }
  },

  /**
   * Get all activities for a specific user
   */
  getByUser: async (
    userEmail: string,
    limit: number = 50,
  ): Promise<ActivityLogEntry[]> => {
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .eq('user_email', userEmail.toLowerCase())
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        console.error('Activity log fetch error:', error)
        return []
      }
      return data || []
    } catch {
      return []
    }
  },

  /**
   * Get all activities (admin view)
   */
  getAll: async (limit: number = 200): Promise<ActivityLogEntry[]> => {
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) return []
      return data || []
    } catch {
      return []
    }
  },

  /**
   * Get activities by type
   */
  getByType: async (
    activityType: ActivityType,
    limit: number = 100,
  ): Promise<ActivityLogEntry[]> => {
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select('*')
        .eq('activity_type', activityType)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) return []
      return data || []
    } catch {
      return []
    }
  },
}

// ─── HELPER FUNCTIONS ─────────────────────────────────────────────────────────

/**
 * Quick log helpers for common activities
 */
export const logActivity = {
  register: (email: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'register',
      description: `New account registered: ${email}`,
      description_ar: `تم تسجيل حساب جديد: ${email}`,
      performed_by: 'user',
    }),

  login: (email: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'login',
      description: `User logged in successfully`,
      description_ar: `تم تسجيل الدخول بنجاح`,
      performed_by: 'user',
    }),

  loginFailed: (email: string, reason: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'login_failed',
      description: `Login failed: ${reason}`,
      description_ar: `فشل تسجيل الدخول: ${reason}`,
      metadata: { reason },
      performed_by: 'user',
    }),

  logout: (email: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'logout',
      description: `User logged out`,
      description_ar: `تم تسجيل الخروج`,
      performed_by: 'user',
    }),

  paymentAttempt: (
    email: string,
    plan: string,
    billingPeriod: string,
    country: string,
  ) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'payment_attempt',
      description: `Payment attempt: ${plan} (${billingPeriod}) - ${country}`,
      description_ar: `محاولة دفع: ${plan} (${billingPeriod === 'monthly' ? 'شهري' : 'سنوي'}) - ${country}`,
      metadata: { plan, billingPeriod, country },
      performed_by: 'user',
    }),

  paymentSuccess: (email: string, plan: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'payment_success',
      description: `Payment successful for plan: ${plan}`,
      description_ar: `تم الدفع بنجاح للخطة: ${plan}`,
      metadata: { plan },
      performed_by: 'user',
    }),

  paymentFailed: (email: string, plan: string, reason?: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'payment_failed',
      description: `Payment failed for plan: ${plan}${reason ? ` - ${reason}` : ''}`,
      description_ar: `فشل الدفع للخطة: ${plan}${reason ? ` - ${reason}` : ''}`,
      metadata: { plan, reason },
      performed_by: 'user',
    }),

  planChange: (
    email: string,
    oldPlan: string,
    newPlan: string,
    changedBy: string,
  ) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'plan_change',
      description: `Plan changed from "${oldPlan}" to "${newPlan}" by ${changedBy}`,
      description_ar: `تم تغيير الخطة من "${oldPlan}" إلى "${newPlan}" بواسطة ${changedBy}`,
      metadata: { oldPlan, newPlan, changedBy },
      performed_by: changedBy,
    }),

  accountSuspended: (email: string, by: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'account_suspended',
      description: `Account suspended by ${by}`,
      description_ar: `تم تعليق الحساب بواسطة ${by}`,
      performed_by: by,
    }),

  accountActivated: (email: string, by: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'account_activated',
      description: `Account activated by ${by}`,
      description_ar: `تم تفعيل الحساب بواسطة ${by}`,
      performed_by: by,
    }),

  botToggle: (email: string, enabled: boolean, by: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: enabled ? 'bot_enabled' : 'bot_disabled',
      description: `Bot ${enabled ? 'enabled' : 'disabled'} by ${by}`,
      description_ar: `تم ${enabled ? 'تفعيل' : 'تعطيل'} البوت بواسطة ${by}`,
      performed_by: by,
    }),

  ticketCreated: (email: string, subject: string, by: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'ticket_created',
      description: `Support ticket created: "${subject}" by ${by}`,
      description_ar: `تم إنشاء تذكرة دعم: "${subject}" بواسطة ${by}`,
      metadata: { subject },
      performed_by: by,
    }),

  adminAction: (email: string, action: string, adminEmail: string) =>
    dbActivityLog.log({
      user_email: email,
      activity_type: 'admin_action',
      description: `Admin action: ${action}`,
      description_ar: `إجراء إداري: ${action}`,
      metadata: { action, adminEmail },
      performed_by: adminEmail,
    }),
}
